/**
 * Closed catalog of record types a collection campaign can ask people to enter.
 * A new module is a row here (page, owner role, search fields), not a new task system.
 */

export const COLLECTION_TARGET_TYPES = Object.freeze([
    'Vendor',
    'Asset',
    'RiskRegister',
    'Process',
    'Control',
    'Threat',
    'Weakness',
    'Bia',
    'Bcp',
    'Training',
    'Objective',
    'Context',
    'Competence',
    'Document',
    'PersonalDataInventory',
    'PrivacyNotice',
    'DpiaAssessment',
    'DataProcessingAgreement',
    'ServiceCatalogItem',
    'AuditFinding',
    'Meeting',
    'Communication',
    'Kpi',
    'StrategicInitiative',
    'InterestedParty',
    'ScopeDocument',
    'Action',
    'Assessment',
    'Gap',
    'AssetLicense',
    'ExplicitConsent',
    'DataSubjectRequest',
    'DataBreach',
    'Incident',
    'ChangeRequest',
    'Problem',
]);

function target(spec) {
    return Object.freeze({
        linkRole: 'READER',
        linkFallbackRole: null,
        ownerRole: 'OWNER',
        ...spec,
        searchFields: Object.freeze(spec.searchFields),
        labelFields: Object.freeze(spec.labelFields),
    });
}

/** @type {Record<string, {
 *   entityType: string,
 *   pageKey: string,
 *   ownerRole: string|null,
 *   linkRole: string|null,
 *   linkFallbackRole: string|null,
 *   partyEntityType: string|null,
 *   searchFields: string[],
 *   labelFields: string[],
 * }>} */
export const COLLECTION_TARGETS = Object.freeze({
    Vendor: target({
        entityType: 'Vendor',
        pageKey: 'vendorManagementVendors',
        ownerRole: 'VENDOR_OWNER',
        linkRole: 'RELATIONSHIP_MANAGER',
        linkFallbackRole: 'READER',
        partyEntityType: 'vendor',
        searchFields: ['name', 'code', 'taxNumber'],
        labelFields: ['name', 'code'],
    }),
    Asset: target({
        entityType: 'Asset',
        pageKey: 'assetInventoryAssets',
        partyEntityType: 'asset',
        searchFields: ['assetName', 'assetId'],
        labelFields: ['assetName', 'assetId'],
    }),
    RiskRegister: target({
        entityType: 'RiskRegister',
        pageKey: 'riskOperationsRiskRegister',
        partyEntityType: 'risk',
        searchFields: ['title', 'riskId', 'description'],
        labelFields: ['title', 'riskId'],
    }),
    Process: target({
        entityType: 'Process',
        pageKey: 'definitionsGeneralSystemProcess',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    Control: target({
        entityType: 'Control',
        pageKey: 'internalControlControl',
        partyEntityType: 'control',
        searchFields: ['control', 'controlCode', 'description'],
        labelFields: ['control', 'controlCode'],
    }),
    Threat: target({
        entityType: 'Threat',
        pageKey: 'ismsControlsThreatIntelligence',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['threatName', 'description'],
        labelFields: ['threatName'],
    }),
    Weakness: target({
        entityType: 'Weakness',
        pageKey: 'ismsControlsVulnerability',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'description'],
        labelFields: ['name'],
    }),
    Bia: target({
        entityType: 'Bia',
        pageKey: 'bcmBia',
        ownerRole: 'AUTHOR',
        partyEntityType: 'bia',
        searchFields: ['name', 'biaId'],
        labelFields: ['name', 'biaId'],
    }),
    Bcp: target({
        entityType: 'Bcp',
        pageKey: 'bcmBcp',
        partyEntityType: 'bcp',
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    Training: target({
        entityType: 'Training',
        pageKey: 'isoTraining',
        partyEntityType: 'training',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Objective: target({
        entityType: 'Objective',
        pageKey: 'isoObjective',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code', 'title'],
        labelFields: ['name', 'title', 'code'],
    }),
    Context: target({
        entityType: 'Context',
        pageKey: 'isoContext',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'title'],
        labelFields: ['name', 'title'],
    }),
    Competence: target({
        entityType: 'Competence',
        pageKey: 'isoCompetence',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    Document: target({
        entityType: 'Document',
        pageKey: 'reportDocument',
        partyEntityType: 'document',
        searchFields: ['title', 'code', 'documentName'],
        labelFields: ['title', 'documentName', 'code'],
    }),
    PersonalDataInventory: target({
        entityType: 'PersonalDataInventory',
        pageKey: 'assetInventoryPdInventory',
        partyEntityType: 'personaldatainventory',
        searchFields: ['activity', 'activityCode', 'personalData'],
        labelFields: ['activity', 'activityCode'],
    }),
    PrivacyNotice: target({
        entityType: 'PrivacyNotice',
        pageKey: 'gdprPrivacyNotice',
        partyEntityType: 'privacynotice',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    DpiaAssessment: target({
        entityType: 'DpiaAssessment',
        pageKey: 'gdprDpiaAssessment',
        partyEntityType: 'dpiaassessment',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    DataProcessingAgreement: target({
        entityType: 'DataProcessingAgreement',
        pageKey: 'gdprDpa',
        partyEntityType: 'dataprocessingagreement',
        searchFields: ['title', 'name', 'code'],
        labelFields: ['title', 'name', 'code'],
    }),
    ServiceCatalogItem: target({
        entityType: 'ServiceCatalogItem',
        pageKey: 'itsmServiceCatalog',
        partyEntityType: 'servicecatalogitem',
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    AuditFinding: target({
        entityType: 'AuditFinding',
        pageKey: 'auditFinding',
        ownerRole: 'FINDING_OWNER',
        linkRole: 'AUDITOR',
        partyEntityType: 'auditfinding',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Meeting: target({
        entityType: 'Meeting',
        pageKey: 'isoMeeting',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Communication: target({
        entityType: 'Communication',
        pageKey: 'isoCommunication',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['contactSubject', 'name'],
        labelFields: ['contactSubject', 'name'],
    }),
    Kpi: target({
        entityType: 'Kpi',
        pageKey: 'isoObjective',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    StrategicInitiative: target({
        entityType: 'StrategicInitiative',
        pageKey: 'isoObjective',
        partyEntityType: 'strategicinitiative',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    InterestedParty: target({
        entityType: 'InterestedParty',
        pageKey: 'contextManagementInterestedParties',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name'],
        labelFields: ['name'],
    }),
    ScopeDocument: target({
        entityType: 'ScopeDocument',
        pageKey: 'contextManagementScopeDocument',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Action: target({
        entityType: 'Action',
        pageKey: 'riskOperationsTreatment',
        partyEntityType: 'action',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Assessment: target({
        entityType: 'Assessment',
        pageKey: 'internalAuditAssessment',
        partyEntityType: 'assessment',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Gap: target({
        entityType: 'Gap',
        pageKey: 'internalControlGap',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    AssetLicense: target({
        entityType: 'AssetLicense',
        pageKey: 'assetInventoryLicenseManagement',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['name', 'code'],
        labelFields: ['name', 'code'],
    }),
    ExplicitConsent: target({
        entityType: 'ExplicitConsent',
        pageKey: 'gdprObtainingExplicitConsent',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['nameSurname', 'referenceNumber'],
        labelFields: ['nameSurname', 'referenceNumber'],
    }),
    DataSubjectRequest: target({
        entityType: 'DataSubjectRequest',
        pageKey: 'gdprDataSubjectRequest',
        ownerRole: 'HANDLER',
        linkRole: 'READER',
        partyEntityType: 'datasubjectrequest',
        searchFields: ['referenceNumber', 'title'],
        labelFields: ['referenceNumber', 'title'],
    }),
    DataBreach: target({
        entityType: 'DataBreach',
        pageKey: 'gdprDataBreach',
        ownerRole: 'DPO',
        linkRole: 'READER',
        partyEntityType: 'databreach',
        searchFields: ['title', 'referenceNumber'],
        labelFields: ['title', 'referenceNumber'],
    }),
    Incident: target({
        entityType: 'Incident',
        pageKey: 'incidentManagementIncidents',
        ownerRole: 'REQUESTER',
        linkRole: 'ASSIGNEE',
        partyEntityType: 'incident',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    ChangeRequest: target({
        entityType: 'ChangeRequest',
        pageKey: 'itsmChangeRequest',
        partyEntityType: 'changerequest',
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
    Problem: target({
        entityType: 'Problem',
        pageKey: 'itsmProblem',
        ownerRole: null,
        linkRole: null,
        partyEntityType: null,
        searchFields: ['title', 'code'],
        labelFields: ['title', 'code'],
    }),
});

export function getCollectionTarget(entityType) {
    const key = entityType == null ? '' : String(entityType);
    return COLLECTION_TARGETS[key] || null;
}

const TARGET_GROUPS = Object.freeze({
    Vendor: 'assets',
    Asset: 'assets',
    AssetLicense: 'assets',
    ServiceCatalogItem: 'assets',
    RiskRegister: 'risk',
    Threat: 'risk',
    Weakness: 'risk',
    Control: 'risk',
    Action: 'risk',
    Gap: 'risk',
    Assessment: 'risk',
    AuditFinding: 'risk',
    Process: 'organization',
    Context: 'organization',
    Competence: 'organization',
    Objective: 'organization',
    Kpi: 'organization',
    StrategicInitiative: 'organization',
    Training: 'organization',
    Meeting: 'organization',
    Communication: 'organization',
    InterestedParty: 'organization',
    ScopeDocument: 'organization',
    Bia: 'continuity',
    Bcp: 'continuity',
    PersonalDataInventory: 'privacy',
    PrivacyNotice: 'privacy',
    DpiaAssessment: 'privacy',
    DataProcessingAgreement: 'privacy',
    ExplicitConsent: 'privacy',
    DataSubjectRequest: 'privacy',
    DataBreach: 'privacy',
    Document: 'documents',
    Incident: 'cases',
    ChangeRequest: 'cases',
    Problem: 'cases',
});

export function collectionTargetGroup(entityType) {
    return TARGET_GROUPS[entityType] || 'other';
}
